// ============================================================================
// Ask AI's onboarding card — a step on one onboarding card (item #944, the
// onboarding part; TCSD-0531 one tool per part, saved through the screen's own
// function). The server half is supabase/functions/am-copilot/onboarding.ts.
//
// Every save here is patchOnboarding (hr-onboarding/hooks/use-onboarding-data.ts),
// the one save the onboarding panel calls, with the panel's own patch: Move to
// stage ({ status }), a checklist item (the checklist merged back into the
// stored blob, metadata kept), Add note (appended with the day), Archive
// ({ status: 'Cancelled' }) and Restore ({ status: 'Offer Accepted' }).
// The card is read again at Do it, so a change made since the draft is kept.
// ============================================================================

import { supabase } from '../../shared/supabase';
import { patchOnboarding } from '../hr-onboarding/hooks/use-onboarding-data';
import { appendNote, effectiveChecklist, mergeChecklistIntoRaw, parseChecklist, visibleItems, withItemStatus } from '../hr-onboarding/utils';
import { CHECKLIST_SECTIONS, stageName, type ChecklistStatus, type OnboardingType } from '../hr-onboarding/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { onboardingButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const ONBOARDING_ACTIONS = ['move_stage', 'set_checklist_item', 'add_note', 'archive', 'restore'] as const;
export type OnboardingAction = (typeof ONBOARDING_ACTIONS)[number];

/** A draft as the server persisted it (prepareOnboardingAction). */
export interface OnboardingDraft {
  onboarding_id: string;
  action: OnboardingAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface OnboardingCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => str(v).trim().length === 0;
const ctxOf = (d: OnboardingDraft) => (d.context ?? {}) as Record<string, unknown>;
const kindOf = (d: OnboardingDraft): OnboardingType => (str(ctxOf(d).kind) === 'W2' ? 'W2' : 'Subcontractor');
const STATUS_WORDS: Record<string, string> = { pending: 'Pending', complete: 'Complete', blocked: 'Blocked', n_a: 'N/A' };
const sourceOf = (id: string): 'employee' | 'partner' => (id.startsWith('EON-') ? 'employee' : 'partner');
/** "Onboarding" on a W2 hire's card, "setup" on a sub's — the panel's own word. */
const wordOf = (d: OnboardingDraft) => (kindOf(d) === 'W2' ? 'onboarding' : 'setup');

/** The person or company the card is for, as the board names them. */
export const onboardingOf = (d: OnboardingDraft) => str(ctxOf(d).name) || str(d.display_name) || d.onboarding_id;

/** What the card shows for this draft, with the person's edits `f`. */
export function onboardingCard(d: OnboardingDraft, f: Record<string, unknown>): OnboardingCardSpec {
  const c = ctxOf(d);
  const now = { label: 'Now', value: str(c.status_words) || '—' };
  const base: OnboardingCardSpec = { title: '', fields: [], info: [now], readBack: null, doItLabel: onboardingButtonWords(d.action, f, kindOf(d)), missing: null };
  switch (d.action) {
    case 'move_stage':
      return { ...base, title: `Move to ${stageName(str(f.to), kindOf(d)) ?? str(f.to)}` };
    case 'set_checklist_item':
      return {
        ...base,
        title: `${str(c.item_label) || str(f.key)}: ${STATUS_WORDS[str(f.status)] ?? str(f.status)}`,
        info: [now, { label: 'Item now', value: STATUS_WORDS[str(c.item_status)] ?? 'Pending' }],
      };
    case 'add_note':
      return {
        ...base,
        title: 'Add note',
        fields: [{ key: 'text', label: 'Note (added to the card’s notes, with today’s date)', kind: 'textarea', required: true }],
        missing: blank(f.text) ? 'Write the note, please.' : null,
      };
    case 'archive':
      return { ...base, title: `Archive ${wordOf(d)}`, readBack: 'This sets it to Cancelled and takes the card off the active board. It can be restored.' };
    case 'restore':
      return { ...base, title: `Restore ${wordOf(d)}`, readBack: `This puts the card back on the board at ${stageName('Offer Accepted', kindOf(d))}.` };
  }
}

export interface OnboardingCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): OnboardingCommitResult => ({ ok: false, error });

/** The patch the panel would write for this step on the card as it is now. */
export function onboardingPatch(
  d: OnboardingDraft,
  f: Record<string, unknown>,
  row: { checklist_json: Record<string, unknown> | null; notes: string | null },
  today: string,
): { patch: Record<string, unknown> } | { error: string } {
  switch (d.action) {
    case 'move_stage':
      return { patch: { status: str(f.to) } };
    case 'set_checklist_item': {
      const checklist = effectiveChecklist(parseChecklist(row.checklist_json));
      const shown = CHECKLIST_SECTIONS.flatMap((s) => visibleItems(checklist[s], kindOf(d)));
      if (!shown.some((it) => it.key === str(f.key))) return { error: 'That item is not on this card’s checklist.' };
      const next = withItemStatus(checklist, str(f.key), str(f.status) as ChecklistStatus);
      return { patch: { checklist_json: mergeChecklistIntoRaw(row.checklist_json, next) } };
    }
    case 'add_note':
      return { patch: { notes: appendNote(row.notes, str(f.text).trim(), today) } };
    case 'archive':
      return { patch: { status: 'Cancelled' } };
    case 'restore':
      return { patch: { status: 'Offer Accepted' } };
  }
}

/** Save one onboarding draft through the onboarding panel's own save. */
export async function commitOnboardingAction(d: OnboardingDraft, f: Record<string, unknown>): Promise<OnboardingCommitResult> {
  const missing = onboardingCard(d, f).missing;
  if (missing) return fail(missing);
  const id = d.onboarding_id;
  const source = sourceOf(id);
  const table = source === 'employee' ? 'employee_onboarding' : 'partner_onboarding';
  try {
    const { data: row, error } = await supabase.from(table).select('checklist_json, notes').eq('onboarding_id', id).maybeSingle();
    if (error) throw error;
    if (!row) return fail('That onboarding card is no longer there.');
    // The panel stamps a note with the day as toISOString gives it; the same here.
    const out = onboardingPatch(d, f, row as { checklist_json: Record<string, unknown> | null; notes: string | null }, new Date().toISOString().slice(0, 10));
    if ('error' in out) return fail(out.error);
    await patchOnboarding(source, id, out.patch);
    const name = onboardingOf(d);
    const notes: Record<OnboardingAction, string> = {
      move_stage: `${name} → ${stageName(str(f.to), kindOf(d)) ?? str(f.to)}.`,
      set_checklist_item: `${name} — checklist updated.`,
      add_note: `${name} — note added.`,
      archive: `${name} — archived.`,
      restore: `${name} — back on the board.`,
    };
    const { checklist_json: _omit, ...shown } = out.patch;
    return {
      ok: true,
      note: notes[d.action],
      target_table: table,
      target_row_id: id,
      committed_payload: d.action === 'set_checklist_item' ? { key: str(f.key), status: str(f.status) } : shown,
    };
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
